/** Volunteer filters for the management {@link DataTable}. */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/forms/Button';
import { SearchInput } from '@/components/forms/SearchInput';
import { SegmentedControl } from '@/components/forms/SegmentedControl';
import { Select } from '@/components/forms/Select';
import { DateRangeInput } from '@/components/forms/DateRangeInput';
import {
  VOLUNTEER_EXIT_REASON_OPTIONS,
  WEEKDAY_FILTER_OPTIONS,
  STAFF_STATUS_CONFIG,
  DATE_RANGE_PERIOD_OPTIONS,
  type VolunteerFilters,
} from './volunteer-configs';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { useEstablishmentsAutocomplete } from '@/hooks/useEstablishmentsAutocomplete';
import { getEstablishment } from '@/services/establishments.service';
import { formatEstablishmentSelectLabel } from '@/utils/format-establishment-select-label-utils';
import { useSubjectsSelect } from '@/hooks/useSubjectsSelect';
/** Props for {@link VolunteerFiltersContent}. */
interface VolunteerFiltersContentProps {
  filters: VolunteerFilters;
  allowedFilterIds: string[];
  visibleFilterIds: string[];
  onSetSearch?: (value: string | undefined) => void;
  onToggleStatus: (status: string) => void;
  onSetSubject: (id: string | undefined) => void;
  onToggleAvailability: (day: string) => void;
  onSetEstablishment: (id: string | undefined) => void;
  onSetDateRange: (range: VolunteerFilters['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
  onSetEntryDateRange?: (from: string | undefined, to: string | undefined) => void;
  onSetEngagementStartDateRange?: (from: string | undefined, to: string | undefined) => void;
  onSetEngagementEndDateRange?: (from: string | undefined, to: string | undefined) => void;
  onSetExitDateRange?: (from: string | undefined, to: string | undefined) => void;
  onSetExitReason?: (reason: string | undefined) => void;
}

/** Filter group is allowed by RBAC and currently toggled on in the configure panel. */
function isVisible(
  filterId: string,
  allowedFilterIds: string[],
  visibleFilterIds: string[],
): boolean {
  return allowedFilterIds.includes(filterId) && visibleFilterIds.includes(filterId);
}

/** Debounced name / email / phone search; empty string becomes `undefined`. */
const SearchFilter: React.FC<{
  filters: VolunteerFilters;
  onSetSearch: (value: string | undefined) => void;
}> = ({ filters, onSetSearch }) => (
  <div className="filter-group">
    <span className="filter-group-label">Recherche</span>
    <div className="w-64">
      <SearchInput
        size="sm"
        shortcut={false}
        debounceMs={300}
        placeholder="Nom, prénom, email ou téléphone..."
        value={filters.search ?? ''}
        onSearch={(q) => onSetSearch(q.trim() || undefined)}
        onClear={() => onSetSearch(undefined)}
      />
    </div>
  </div>
);

/** Staff status chip filter. */
const StatusFilter: React.FC<{
  filters: VolunteerFilters;
  onToggleStatus: (status: string) => void;
}> = ({ filters, onToggleStatus }) => (
  <div className="filter-group">
    <span className="filter-group-label">Statut</span>
    <div className="filter-options">
      {Object.entries(STAFF_STATUS_CONFIG).map(([key, config]) => {
        const Icon = config.icon;
        const isActive = filters.statuses.includes(key);
        return (
          <Button
            key={key}
            variant="custom"
            size="sm"
            icon={Icon}
            iconPosition="left"
            className={cn(
              'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg transition-all',
              isActive ? 'filter-badge-active' : 'filter-badge-inactive',
            )}
            style={
              {
                '--filter-color': config.badgeColor ?? 'rgb(var(--status-neutral))',
              } as React.CSSProperties
            }
            onClick={() => onToggleStatus(key)}
          >
            {config.label}
          </Button>
        );
      })}
    </div>
  </div>
);

/** Subject select filter. */
const SubjectFilter: React.FC<{
  filters: VolunteerFilters;
  onSetSubject: (id: string | undefined) => void;
  enabled: boolean;
}> = ({ filters, onSetSubject, enabled }) => {
  const { options, isLoading, onLoadMore, hasMore } = useSubjectsSelect({ enabled });

  const selectOptions = [{ value: '', label: 'Toutes' }, ...options];

  return (
    <div className="filter-group">
      <span className="filter-group-label">Matière</span>
      <Select
        options={selectOptions}
        value={filters.subjects[0] ?? ''}
        onChange={(val) => onSetSubject(val || undefined)}
        onLoadMore={onLoadMore}
        hasMore={hasMore}
        isLoading={isLoading}
        size="sm"
        className="min-w-36"
      />
    </div>
  );
};

/** Weekday availability chip filter. */
const AvailabilityFilter: React.FC<{
  filters: VolunteerFilters;
  onToggleAvailability: (day: string) => void;
}> = ({ filters, onToggleAvailability }) => {
  return (
    <div className="filter-group">
      <span className="filter-group-label">Disponibilité</span>
      <div className="filter-options">
        {WEEKDAY_FILTER_OPTIONS.map((day) => {
          const isActive = filters.availability?.includes(day.value) ?? false;
          return (
            <Button
              key={day.value}
              variant="custom"
              size="sm"
              icon={Calendar}
              iconPosition="left"
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border transition-all',
                isActive ? 'filter-option-secondary--active' : 'filter-option-secondary',
              )}
              onClick={() => onToggleAvailability(day.value)}
            >
              {day.label}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

/** Establishment autocomplete filter. */
const EstablishmentFilter: React.FC<{
  filters: VolunteerFilters;
  onSetEstablishment: (id: string | undefined) => void;
}> = ({ filters, onSetEstablishment }) => {
  const { options, isLoading, onSearch, onLoadMore, hasMore } = useEstablishmentsAutocomplete();

  const { data: selectedEstablishment } = useQuery({
    queryKey: ['establishment', filters.establishmentId],
    queryFn: () => getEstablishment(filters.establishmentId!),
    enabled: Boolean(filters.establishmentId),
  });

  const mergedOptions = React.useMemo(() => {
    const id = filters.establishmentId;
    if (!id || !selectedEstablishment) return options;
    const exists = options.some((o) => o.value === id);
    if (exists) return options;
    return [
      {
        value: selectedEstablishment.id,
        label: formatEstablishmentSelectLabel(selectedEstablishment),
      },
      ...options,
    ];
  }, [filters.establishmentId, options, selectedEstablishment]);

  return (
    <div className="filter-group">
      <span className="filter-group-label">Établissement</span>
      <div className="w-64">
        <Autocomplete
          placeholder="Rechercher un établissement..."
          options={mergedOptions}
          value={filters.establishmentId ?? ''}
          onChange={(val) => onSetEstablishment(val || undefined)}
          onSearch={onSearch}
          onLoadMore={onLoadMore}
          hasMore={hasMore}
          isLoading={isLoading}
          minSearchLength={2}
          inputSize="sm"
        />
      </div>
    </div>
  );
};

/** Creation date range filter. */
const CreationDateFilter: React.FC<{
  filters: VolunteerFilters;
  onSetDateRange: (range: VolunteerFilters['dateRange']) => void;
  onSetCustomDateRange: (from: Date | undefined, to: Date | undefined) => void;
}> = ({ filters, onSetDateRange, onSetCustomDateRange }) => {
  const toDateString = (d?: Date) => {
    if (!d) return undefined;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fromStr = toDateString(filters.customDateFrom);
  const toStr = toDateString(filters.customDateTo);

  const handleFromChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(undefined, filters.customDateTo);
      return;
    }
    const [year, month, day] = val.split('-').map(Number);
    const d = new Date(year, month - 1, day, 0, 0, 0);
    onSetCustomDateRange(d, filters.customDateTo);
    onSetDateRange('all');
  };

  const handleToChange = (val: string) => {
    if (!val) {
      onSetCustomDateRange(filters.customDateFrom, undefined);
      return;
    }
    const [year, month, day] = val.split('-').map(Number);
    const d = new Date(year, month - 1, day, 23, 59, 59);
    onSetCustomDateRange(filters.customDateFrom, d);
    onSetDateRange('all');
  };

  return (
    <div className="filter-group">
      <span className="filter-group-label">Date de création</span>
      <div className="flex items-center gap-2">
        <SegmentedControl
          size="sm"
          value={filters.customDateFrom ? '' : (filters.dateRange ?? 'all')}
          onChange={(v) => {
            onSetDateRange(v as VolunteerFilters['dateRange']);
            onSetCustomDateRange(undefined, undefined);
          }}
          options={[...DATE_RANGE_PERIOD_OPTIONS]}
        />
        {filters.dateRange === 'all' && (
          <div className="w-auto">
            <DateRangeInput
              startDate={fromStr}
              endDate={toStr}
              onStartDateChange={handleFromChange}
              onEndDateChange={handleToChange}
              layout="horizontal"
              showConnector={true}
              showPresets={false}
              hidePickerLabels={true}
              compact={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

/** Volunteer filters for the management {@link DataTable}. */
export const VolunteerFiltersContent: React.FC<VolunteerFiltersContentProps> = ({
  filters,
  allowedFilterIds,
  visibleFilterIds,
  onSetSearch,
  onToggleStatus,
  onSetSubject,
  onToggleAvailability,
  onSetEstablishment,
  onSetDateRange,
  onSetCustomDateRange,
  onSetEntryDateRange,
  onSetEngagementStartDateRange,
  onSetEngagementEndDateRange,
  onSetExitDateRange,
  onSetExitReason,
}) => {
  const show = (id: string) => isVisible(id, allowedFilterIds, visibleFilterIds);

  return (
    <div className="filter-groups-container">
      {show('filter-search') && onSetSearch && (
        <SearchFilter filters={filters} onSetSearch={onSetSearch} />
      )}

      {show('filter-status') && <StatusFilter filters={filters} onToggleStatus={onToggleStatus} />}

      {show('filter-subject') && (
        <SubjectFilter filters={filters} onSetSubject={onSetSubject} enabled={true} />
      )}

      {show('filter-availability') && (
        <AvailabilityFilter filters={filters} onToggleAvailability={onToggleAvailability} />
      )}

      {show('filter-establishment') && (
        <EstablishmentFilter filters={filters} onSetEstablishment={onSetEstablishment} />
      )}

      {show('filter-dates') && (
        <CreationDateFilter
          filters={filters}
          onSetDateRange={onSetDateRange}
          onSetCustomDateRange={onSetCustomDateRange}
        />
      )}

      {show('filter-entry-date') && onSetEntryDateRange && (
        <div className="filter-group">
          <span className="filter-group-label">Date d'entrée VSC</span>
          <div className="w-auto">
            <DateRangeInput
              startDate={filters.entryDateFrom}
              endDate={filters.entryDateTo}
              onStartDateChange={(val) =>
                onSetEntryDateRange(val || undefined, filters.entryDateTo)
              }
              onEndDateChange={(val) =>
                onSetEntryDateRange(filters.entryDateFrom, val || undefined)
              }
              layout="horizontal"
              showConnector={true}
              showPresets={false}
              hidePickerLabels={true}
              compact={true}
            />
          </div>
        </div>
      )}

      {show('filter-mission-start-date') && onSetEngagementStartDateRange && (
        <div className="filter-group">
          <span className="filter-group-label">Date de début engagement</span>
          <div className="w-auto">
            <DateRangeInput
              startDate={filters.engagementStartDateFrom}
              endDate={filters.engagementStartDateTo}
              onStartDateChange={(val) =>
                onSetEngagementStartDateRange(val || undefined, filters.engagementStartDateTo)
              }
              onEndDateChange={(val) =>
                onSetEngagementStartDateRange(filters.engagementStartDateFrom, val || undefined)
              }
              layout="horizontal"
              showConnector={true}
              showPresets={false}
              hidePickerLabels={true}
              compact={true}
            />
          </div>
        </div>
      )}

      {show('filter-contract-end-date') && onSetEngagementEndDateRange && (
        <div className="filter-group">
          <span className="filter-group-label">Date de fin engagement</span>
          <div className="w-auto">
            <DateRangeInput
              startDate={filters.engagementEndDateFrom}
              endDate={filters.engagementEndDateTo}
              onStartDateChange={(val) =>
                onSetEngagementEndDateRange(val || undefined, filters.engagementEndDateTo)
              }
              onEndDateChange={(val) =>
                onSetEngagementEndDateRange(filters.engagementEndDateFrom, val || undefined)
              }
              layout="horizontal"
              showConnector={true}
              showPresets={false}
              hidePickerLabels={true}
              compact={true}
            />
          </div>
        </div>
      )}

      {show('filter-exit-date') && onSetExitDateRange && (
        <div className="filter-group">
          <span className="filter-group-label">Date de désactivation</span>
          <div className="w-auto">
            <DateRangeInput
              startDate={filters.exitDateFrom}
              endDate={filters.exitDateTo}
              onStartDateChange={(val) => onSetExitDateRange(val || undefined, filters.exitDateTo)}
              onEndDateChange={(val) => onSetExitDateRange(filters.exitDateFrom, val || undefined)}
              layout="horizontal"
              showConnector={true}
              showPresets={false}
              hidePickerLabels={true}
              compact={true}
            />
          </div>
        </div>
      )}

      {show('filter-exit-reason') && onSetExitReason && (
        <div className="filter-group">
          <span className="filter-group-label">Motif de sortie</span>
          <div className="w-64">
            <Select
              key={filters.exitReason ?? '__empty__'}
              options={[...VOLUNTEER_EXIT_REASON_OPTIONS]}
              value={filters.exitReason ?? ''}
              onChange={(val) => onSetExitReason(val || undefined)}
              placeholder="Tous les motifs"
              size="sm"
            />
          </div>
        </div>
      )}
    </div>
  );
};
